import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { api } from '../services/api'

/**
 * Admin Authentication Context
 * Completely separate from user website authentication
 * 
 * In production: Replace mock logic with real API calls
 * The interface remains identical, enabling seamless backend integration
 */

const AuthContext = createContext(null)

// ============================================================
// STORED AUTH KEYS
// ============================================================
// AUTH PROVIDER
// ============================================================
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // Check for existing session on mount
    useEffect(() => {
        const checkAuth = async () => {
            try {
                // In production: Validate session token with backend
                const storedUser = localStorage.getItem('admin_user')
                if (storedUser) {
                    setUser(JSON.parse(storedUser))
                }
            } catch (err) {
                console.error('Auth check failed:', err)
                localStorage.removeItem('admin_user')
            } finally {
                setLoading(false)
            }
        }

        checkAuth()
    }, [])

    /**
     * Finalizes a session from a successful login/2FA-verify API response.
     */
    const applySession = (res) => {
        // Backend derives role from is_admin=true → 'admin', not 'super_admin'
        const isAdmin = res.success && (res.data.user.isAdmin || res.data.user.role === 'admin')

        if (isAdmin) {
            const adminUser = {
                ...res.data.user,
                role: 'admin',
                permissions: ['users', 'subscriptions', 'tenders', 'ingestion', 'alerts']
            }
            setUser(adminUser)
            localStorage.setItem('admin_user', JSON.stringify(adminUser))
            localStorage.setItem('admin_token', res.data.token)
            return true
        }

        setError('Access denied: Admin account required.')
        localStorage.removeItem('admin_user')
        localStorage.removeItem('admin_token')
        return false
    }

    /**
     * Login handler. Admin accounts require a follow-up 2FA code, so this
     * returns a status string rather than a plain boolean:
     *   'success'  — logged in
     *   '2fa'      — password OK, verification code has been emailed
     *   'error'    — invalid credentials (see `error` state)
     * @param {string} email
     * @param {string} password
     * @returns {Promise<'success'|'2fa'|'error'>}
     */
    const login = useCallback(async (email, password) => {
        setError(null)
        setLoading(true)

        try {
            const res = await api.post('/auth/login', { email, password, portal: 'admin' })

            if (res.data?.requiresTwoFactor) {
                return '2fa'
            }

            return applySession(res) ? 'success' : 'error'
        } catch (err) {
            setError(err.message || 'Authentication failed')
            return 'error'
        } finally {
            setLoading(false)
        }
    }, [])

    /**
     * Verify the 2FA code emailed to an admin after password login.
     * @param {string} email
     * @param {string} code
     * @returns {Promise<boolean>}
     */
    const verifyTwoFactor = useCallback(async (email, code) => {
        setError(null)
        setLoading(true)

        try {
            const res = await api.post('/auth/verify-2fa', { email, code })
            return applySession(res)
        } catch (err) {
            setError(err.message || 'Invalid or expired code')
            return false
        } finally {
            setLoading(false)
        }
    }, [])

    /**
     * Request a password reset email
     * @param {string} email
     */
    const requestPasswordReset = useCallback(async (email) => {
        const res = await api.post('/auth/forgot-password', { email })
        return res.data
    }, [])

    /**
     * Reset password using a reset token
     * @param {string} token
     * @param {string} newPassword
     */
    const resetPassword = useCallback(async (token, newPassword) => {
        const res = await api.post('/auth/reset-password', { token, newPassword })
        return res.data
    }, [])

    /**
     * Logout handler
     */
    const logout = useCallback(() => {
        setUser(null)
        localStorage.removeItem('admin_user')
        localStorage.removeItem('admin_token')
    }, [])

    /**
     * Check if user has specific permission
     * @param {string} permission 
     * @returns {boolean}
     */
    const hasPermission = useCallback((permission) => {
        if (!user) return false
        if (user.role === 'super_admin') return true
        return user.permissions?.includes(permission) ?? false
    }, [user])

    const value = {
        user,
        loading,
        error,
        isAuthenticated: !!user,
        login,
        verifyTwoFactor,
        logout,
        requestPasswordReset,
        resetPassword,
        hasPermission,
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

/**
 * Auth hook - must be used within AuthProvider
 */
export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
